import type { CSSProperties } from "react";
import { useEntranceMotion } from "@/hooks/useEntranceMotion";

// Effort that starts drifting toward waste, is caught at `turn`, and settles
// at `to` beside the star for a job in a green zone. Offsets are SVG user
// units from just right of the vertical axis. Dots alternate between the make
// and save money stars, so each star takes a landing every 1.6s.
const efforts = [
  { turn: [56, -12], to: [-108, -50], delay: 0 },
  { turn: [60, 10], to: [-108, 92], delay: 0.8 },
  { turn: [48, -24], to: [-50, -76], delay: 1.6 },
  { turn: [52, 22], to: [-50, 70], delay: 2.4 },
  { turn: [62, -4], to: [-86, -30], delay: 3.2 },
  { turn: [58, 4], to: [-84, 116], delay: 4 },
];

// One hand-drawn highlighter pass per quadrant; the lower zone reuses it.
const scribble =
  "M128 74L222 70L126 96L224 92L124 118L224 114L124 140L224 136L126 162L222 158L130 184L218 182";
const star =
  "M0-18L4.4-6.1L17.1-5.6L7.1 2.3L10.6 14.6L0 7.5L-10.6 14.6L-7.1 2.3L-17.1-5.6L-4.4-6.1Z";

/** Animated rendering of the "ROI of AI" sketch: the return comes from saving
 * time that makes or saves money, aimed at a specific job to be done (the
 * stars). Effort that drifts toward waste is steered back into the green.
 * Shares its card, labels, and motion states with StateOfAIExplainer.
 */
const ROIOfAIExplainer = () => {
  const [card, motion] = useEntranceMotion<HTMLElement>();

  return (
    <figure ref={card} className="home-sketch-card home-roi-card" data-motion={motion}>
      <div
        className="home-sketch-plot"
        role="img"
        aria-label="The ROI of AI, drawn as two crossing axes: make money at the top, save money at the bottom, save time on the left, and waste on the right, as lost time, tokens, and leaks. Both quadrants on the save-time side are highlighted green, each with a star marking a job to be done. Effort that starts drifting toward waste is steered back into the green, beside a job."
      >
        <svg viewBox="0 0 520 400" aria-hidden="true">
          <path className="home-roi-zone" d={scribble} pathLength={1} />
          <path className="home-roi-zone" d={scribble} transform="translate(0 146)" pathLength={1} />
          <path className="home-sketch-axis" d="M240 56V344" pathLength={1} />
          <path className="home-sketch-axis" d="M110 200H370" pathLength={1} />
          <g className="home-sketch-dots">
            {efforts.map(({ turn: [turnX, turnY], to: [toX, toY], delay }) => (
              <circle
                key={`${toX} ${toY}`}
                cx="252"
                cy="200"
                r="5"
                style={
                  {
                    "--turn-x": `${turnX}px`,
                    "--turn-y": `${turnY}px`,
                    "--to-x": `${toX}px`,
                    "--to-y": `${toY}px`,
                    "--delay": `${delay}s`,
                  } as CSSProperties
                }
              />
            ))}
          </g>
          <g className="home-roi-target" transform="translate(174 136)">
            <path className="home-roi-star" d={star} />
          </g>
          <g className="home-roi-target" transform="translate(174 282)">
            <path className="home-roi-star" d={star} />
          </g>
        </svg>
        <span className="home-sketch-label" data-pole="make" data-tone="gain">
          <span className="home-sketch-mark">Make money</span>
        </span>
        <span className="home-sketch-label" data-pole="time" data-tone="gain">
          <span className="home-sketch-mark">Save</span>
          <span className="home-sketch-mark">time</span>
        </span>
        <span className="home-sketch-label" data-pole="save" data-tone="gain">
          <span className="home-sketch-mark">Save money</span>
        </span>
        <span className="home-sketch-label" data-pole="waste">
          <span className="home-sketch-mark">Waste</span>
          <span className="home-sketch-mark">time</span>
          <span className="home-sketch-mark">tokens</span>
          <span className="home-sketch-mark">leaks</span>
        </span>
        <span className="home-sketch-label home-roi-job-tag" data-pole="job-make">JTBD</span>
        <span className="home-sketch-label home-roi-job-tag" data-pole="job-save">JTBD</span>
      </div>
      {/* The guide's live-session example, in its "When X, I want Y, so I can Z" frame. */}
      <figcaption className="home-roi-job">
        <span className="home-roi-job-label">A job to be done</span>
        <p>
          When <span className="home-roi-job-part">a newsletter is approved</span>,
          I want to <span className="home-roi-job-part">post it on LinkedIn automatically</span>,
          so I can <span className="home-roi-job-part">drive conversions and traffic</span>.
        </p>
      </figcaption>
    </figure>
  );
};

export default ROIOfAIExplainer;
